import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TuiButton, TuiHint, TuiLoader } from '@taiga-ui/core';
import { WorkflowFormFieldInputPhotos } from '@features/requests/request-form/components/request-form-workflows/types';
import { InputPhotosService } from '@core/services/workflow/input-photos.service';
import { FileItem } from '@core/services/workflow/input-photos.types';
import {
  getWorkflowFileConstraints,
  type PhotoQueueTarget,
} from '@core/services/workflow';
import { TranslatePipe } from '@ngx-translate/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';

@Component({
  selector: 'x-input-photos-field',
  imports: [ReactiveFormsModule, TuiHint, TuiButton, TranslatePipe, TuiLoader],
  templateUrl: './input-photos-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class InputPhotosFieldComponent {
  field = input.required<WorkflowFormFieldInputPhotos>();
  control = input.required<FormControl<FileItem[] | null>>();
  /** Экземпляр поля: заявка, регламент заявки, нода, форма и ключ поля */
  target = input<PhotoQueueTarget | undefined>();
  canExecuteWorkflow = input<boolean>(false);
  photoSelected = output<File[]>();

  fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  private readonly inputPhotosService = inject(InputPhotosService);
  protected $photosCount = computed(() => {
    const target = this.target();

    return target ? this.inputPhotosService.getTargetFiles(target).length : 0;
  });

  /** accept выбора файла из схемы (по умолчанию — изображения) */
  protected readonly $accept = computed(
    () => getWorkflowFileConstraints(this.field(), 'image/*').accept,
  );

  protected pendingCount = toSignal(
    toObservable(this.target).pipe(
      switchMap(target =>
        target
          ? this.inputPhotosService.getPendingCountForTarget$(target)
          : of(0),
      ),
    ),
    { initialValue: 0 },
  );

  selectFiles() {
    if (!this.canExecuteWorkflow()) {
      return;
    }

    this.fileInput().nativeElement.click();
  }

  onFileSelected(event: Event) {
    if (!this.canExecuteWorkflow()) {
      return;
    }

    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);

      this.photoSelected.emit(newFiles);
    }

    // Тот же файл можно выбрать повторно
    input.value = '';
  }
}
